<template>
  <div class="d-none">
    <p>Socket Connected: {{ isConnected }} ({{ socketId }})</p>
    <p>Token: {{ currentToken }} </p>
    <button @click="pingServer()">Ping Server</button>
    <button @click="pingServer()">Disconnect</button>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions, mapState, mapGetters } from "vuex";
import io from "socket.io-client";
import { EventBus } from "../events/eventBus";
import msgpackParser from "socket.io-msgpack-parser";
import SocketHolder from "../SocketHolder";
import { recognitionLanguages } from "../constants";
import formatter from "@/mixins/formatter";
import moment from "moment";

function defaultTimestamp() {
  return new Date().toISOString();
}

export default {
  name: "AgencyListener",
  mixins: [formatter],
  data() {
    return {
      socket: null,
      socketId: null,
      isConnected: false,
      isConnecting: false,
      connectionError: "",
      showConnectionModal: false,
      currentRoute: this.$router.currentRoute.path
    };
  },
  computed: {
    ...mapState({
      serverUrl: (state) => state.connection.serverUrl,
      wsOnly: (state) => state.connection.wsOnly,
      path: (state) => state.connection.path,
      namespace: (state) => state.connection.namespace,
      parser: (state) => state.connection.parser,
      sessionId: (state) => state.user.session,
      currentToken: (state) => state.user.token,
      conversations: (state) => state.conversations.conversations
    }),
    ...mapGetters("agency", ["currentAgency", "agencySettings"]),
    ...mapGetters("user", ["currentUser", "currentSession", "volume", "soundSelectorSetting", "userCallTakerModeEnabled"]),
    ...mapGetters("conversations", ["currentConversation", "activeConversations"]),
    ...mapGetters("transcription", ["transcribing"]),
    ...mapGetters("video", ["videoRooms", "videoRoomWidgetBootstraped"]),
    ...mapGetters("campaigns", ["currentCampaign"]),
  },
  methods: {
      ...mapActions("agency", ["bindAgencySession", "updateAgency", "updateAgencySettings"]),
      ...mapActions("conversations", [
        "createConversation",
        "createMessage",
        "upsertAttachment",
        "fetchConversations",
        "readConversation"
      ]),
      ...mapActions("campaigns", ["upsertCampaignEvent"]),
      pingServer() {
        this.socket.emit("pingServer", "PING!");
      },
      playSound(eventType, eventData = null) {
        let targetConversation = null;
        if (eventData) {
          targetConversation = this.activeConversations.find(convo => convo.conversation_id === eventData.conversation_id);
        }
        switch (eventType) {
          case "new-conversation":
          case "createConversation": {
            if (eventData && targetConversation && (targetConversation.operator_id == null || targetConversation.operator_id == this.currentUser.user_id)) {
              const { channel, number } = eventData;
              if (channel === 'sms' && number !== '911') {
                const { enabled } = this.soundSelectorSetting.textNotificationSound;
                if (enabled) {
                  EventBus.$emit("play-text-notification-sound-user");
                }
              } else if (channel === 'sms' && number === '911') {
                const { enabled } = this.soundSelectorSetting.textTo911NotificationSound;
                if (enabled) {
                  EventBus.$emit("play-text-to-911-notification-sound-user");
                }
              } else if (channel === 'voice') {
                const { enabled } = this.soundSelectorSetting.voiceNotificationSound;
                if (enabled) {
                  EventBus.$emit("play-voice-notification-sound-user");
                }
              }
            }
            break;
          }
          case "new-message":
          case "createMessage": {
            if (eventData && eventData.direction === 'inbound' && eventData.source !== 'transcribe') {
              if (targetConversation && (targetConversation.operator_id == null || targetConversation.operator_id == this.currentUser.user_id)) {
                const { enabled } = this.soundSelectorSetting.messageNotificationSound;
                if (enabled) {
                  EventBus.$emit("play-message-notification-sound-user");
                }
              }
            }
            break;
          }
          case "participant-joined":
          case "addVideoRoomParticipant": {
            const { enabled } = this.soundSelectorSetting.videoJoinedNotificationSound;
            if (enabled) {
              EventBus.$emit("play-video-joined-notification-sound-user");
            }
            break;
          }
          case "participant-left":
          case "deleteVideoRoomParticipant": {
            const { enabled } = this.soundSelectorSetting.videoLeftNotificationSound;
            if (enabled) {
              EventBus.$emit("play-video-left-notification-sound-user");
            }
            break;
          }
        }
      },
      desktopNotify(eventType, eventData) {
        if (this.currentConversation && this.currentConversation.conversation_id == eventData.conversation_id)
          return;

        const targetConversation = this.activeConversations.find(convo => convo.conversation_id === eventData.conversation_id);

        if (targetConversation && (targetConversation.operator_id == null || targetConversation.operator_id == this.currentUser.user_id)) {
          switch (eventType) {
            case "createMessage":  {
              if (eventData.direction == "inbound" && eventData.source == "sms") {
                const { recipient_label, recipient_number, message_body } = eventData;
                const title = `New Message from ${recipient_label ? recipient_label : this.prettyPhoneNumber(recipient_number)}`;
                const body = message_body;
                this.$desktopNotify(title, body);
              }
              break;
            }
            default: {
              // const title = 'Convey911';
              // const body = "Notification from Convey911.\nPlease check in and see if there's anything you should handle.";
              // this.$desktopNotify(title, body);
            }
          }
        }
      },
      tryConnect(serverUrl, namespace, socketId, wsOnly, path, parser) {
        this.isConnecting = true;
        this.$store.commit("connection/tryConnect");
        if (SocketHolder.socket) {
          SocketHolder.socket.disconnect();
          SocketHolder.socket.off("connect");
          SocketHolder.socket.off("connect_error");
          SocketHolder.socket.off("disconnect");
        }

        const socket = io(serverUrl + namespace, {
          forceNew: true,
          reconnection: false,
          withCredentials: ["sandbox", "production", "qa"].includes(process.env.NODE_ENV), // false for dev, true everywhere else - needed for cookie-based sticky-sessions
          transports: wsOnly ? ["websocket"] : ["websocket", "polling"],
          path,
          parser: parser === "msgpack" ? msgpackParser : null,
          auth: { token: `Bearer ${this.currentToken}` },
          socketId
        });

        socket.once("connect", () => {
          this.showConnectionModal = false;
          this.connectionError = "";
          this.isConnecting = false;

          socket.io.reconnection(true);
          this.$store.commit("connection/saveConfig", {
            serverUrl,
            wsOnly,
            path,
            namespace,
            parser,
          });
          SocketHolder.socket = socket;
          this.registerEventListeners(socket);
        });
        socket.on("connect", (socketId = socket.id, timestamp = defaultTimestamp()) => {
          this.$store.commit("connection/connect", { socketId, timestamp });
          if (this.currentAgency && this.currentAgency.agency_id) {
            socket.emit("connectAgencyFeed", { token: this.currentToken, agencyId: this.currentAgency.agency_id, sessionId: this.currentSession.session_id })
            this.fetchConversations();
          }
        });
        socket.on("sessionCreated", async (sessionId) => {
          /* eslint-disable */
          console.log(`[IO:sessionCreated] ${sessionId}`);
          this.$store.commit("user/setUserSession", sessionId);
          this.$store.dispatch("user/fetchPendingInvitations", {
            token: this.currentToken,
            userEmail: this.$auth.user.email,
          });
        });
        socket.on("sessionUser", (user) => {
          this.$store.commit("user/setCurrentUser", user);
        });
        socket.on("sessionRefreshed", () => {
          // this.$auth.refreshTokens();
          window.location.reload();
        });
        socket.on("sessionError", (err) => {
          /* eslint-disable */
          console.log(`[IO:sessionError] ${err}`);
          this.$store.commit('connection/setConnectionError', err);
          this.$router.push({ name: "Error" }).catch(() => {});
        });
        socket.on("connect_error", (err) => {
          if (this.isConnecting || err.message === "not authorized") {
            this.showConnectionModal = true;
            this.connectionError = err.message;
          }
          this.isConnecting = false;
        });
        socket.on("disconnect", (reason) => {
          // this should not be needed, but connection errors with a mismatching parser may trigger in a "disconnect"
          // event instead of a "connect_error" event (needs to be fixed in the client code)
          if (this.isConnecting) {
            // We don't really need this since connected state is persisted through connection/connected store
            this.isConnecting = false;
            this.connectionError = reason;
          }
          this.$store.commit("connection/disconnect");
          EventBus.$emit("socket-connection-disconnected");
        });
      },
      registerEventListeners(socket) {
        socket.on("config", (config) => {
          this.$store.commit("config/updateConfig", config);
        });
        socket.on("server_stats", (serverStats) => {
          this.$store.commit("servers/onServerStats", serverStats);
          this.$store.commit("main/onServerStats", serverStats);
        });
        socket.on("agencyConnected", () => {
          this.$toastr("success", "Agency connected!");
        });
        socket.on("agencySessions", (agencySessions) => {
          agencySessions.forEach((session) => {
            this.$store.commit("agency/addAvailableAgent", {
              agentName: `${session.fname} ${session.lname}`,
              agentId: session.user_id,
              updatedAt: session.updated_at,
              sessionId: session.session_id,
              socketId: socket.id
            });
          })
        });
        socket.on("feedConnected", (status) => {
          this.$store.commit("agency/setFeedStatus", status);
        });
        socket.on("socket_updated", (socket) => {
          this.$store.commit("main/onSocketUpdated", socket);
        });
        socket.on("socket_disconnected", (nsp, id, reason, timestamp = defaultTimestamp()) => {
          this.$store.commit("main/onSocketDisconnected", { timestamp, nsp, id, reason });
        });
        socket.on("room_joined", (nsp, room, id, timestamp = defaultTimestamp()) => {
          this.$store.commit("main/onRoomJoined", { timestamp, nsp, room, id });
        });
        socket.on("room_left", (nsp, room, id, timestamp = defaultTimestamp()) => {
          this.$store.commit("main/onRoomLeft", { timestamp, nsp, room, id });
        });
        socket.on("event_received", (nsp, id, args, timestamp) => {
          this.$store.commit("main/onEventReceived", { timestamp, nsp, id, args });
        });
        socket.on("event_sent", (nsp, id, args, timestamp) => {
          this.$store.commit("main/onEventSent", { timestamp, nsp, id, args });
        });
        socket.on("updateMessageStatus", (data) => {
          this.$store.commit("conversations/setMessageStatus", data);
        });
        socket.on("createConversation", (data) => {
          this.createConversation(data);

          const { operator_id, conversation_id } = data;
          if (operator_id === this.currentUser.user_id && this.userCallTakerModeEnabled && this.agencySettings.enableCalltakerMode) {
            this.$router.push({
              name: "Conversation Detail",
              params: {
                conversationId: conversation_id
              },
            }).catch(() => {});
          }
        });
        socket.on("closeConversation", (data) => {
          this.$store.commit("conversations/updateConversation", data);
          if (this.currentConversation && data.conversation_id === this.currentConversation.conversation_id) {
            this.$store.commit("conversations/setConversation", data);
            // this.$router.push({ name: "Conversations Dashboard" }).catch(() => {});
          }
        });
        socket.on("createMessage", (data) => {
          if (data.source != "transcribe") {
            this.createMessage(data);
            let conv = this.conversations.filter((c) => c.conversation_id === data.conversation_id);
          }
        });
        socket.on("conversationRead", (conversationId) => {
          this.readConversation(conversationId);
        });
        socket.on("notify-agent", (data) => {
          const { apiEvent, eventJson } = data;
          console.log("[IO:notify-agent] " + apiEvent);
          if (!this.currentConversation || this.currentConversation.conversation_id != eventJson.conversation_id) {
            if (apiEvent === 'createConversation') {
              EventBus.$emit('AlertConversation', eventJson.conversation_id);
            } else if (apiEvent === 'createMessage') {
              const { conversation_channel, source } = eventJson;
              if (source === 'transcribe') {
                return;
              }
              EventBus.$emit('AlertConversation', eventJson.conversation_id);
            }

            this.desktopNotify(apiEvent, eventJson);
            this.playSound(apiEvent, eventJson);
          }
        });
        socket.on("addRecipientGeolocation", (data) => {
          console.log("[IO:addRecipientGeolocation] " + JSON.stringify(data));

          this.$store.commit("conversations/updateGeography", [data]);
          EventBus.$emit("addRecipientGeolocation", data);
          EventBus.$emit("receiveGeolocation", data);
        });
        socket.on("updateVideoStatus", (data) => {
          if (data && data.status !== null) {
            const { callback_event } = data;
            console.log("[IO:updateVideoStatus] ", callback_event);

            this.$store.commit("video/upsertVideoRoom", data);

            if (callback_event == "participant-connected") {
              this.$store.commit("video/addParticipant", data);
            }

            if (callback_event == "participant-disconnected") {
              this.$store.commit("video/removeParticipant", data);
            }

            if (callback_event === "room-ended" && data.conversation_id === this.currentConversation.conversation_id) {
              EventBus.$emit("closeVideoModal");
            }
          }
        });
        socket.on("closeVideoRoom", (data) => {
          EventBus.$emit("closeVideoModal");
        });
        socket.on("addVideoRoomParticipant", (data) => {
          console.log("[AgencyListener:addVideoRoomParticipant]", data)
          const { room_id } = data;
          const videoRoom = this.videoRooms.find(room => room.room_id == room_id);

          if (videoRoom) {
            const { conversation_id } = videoRoom;
            const conversation = this.activeConversations.find(i => i.conversation_id == conversation_id);

            if (conversation && (conversation.operator_id == null || conversation.operator_id === this.currentUser.user_id) && data.participant != "Authorized Participant")
              this.playSound("participant-joined");
          }
        });
        socket.on("deleteVideoRoomParticipant", (data) => {
          console.log("[AgencyListener:deleteVideoRoomParticipant]", data)
          const { room_id } = data;
          const videoRoom = this.videoRooms.find(room => room.room_id == room_id);
          if (videoRoom) {
            const { conversation_id } = videoRoom;
            const conversation = this.activeConversations.find(i => i.conversation_id == conversation_id);

            if (conversation && (conversation.operator_id == null || conversation.operator_id === this.currentUser.user_id) && data.participant != "Authorized Participant") {
              this.playSound("participant-left");
            }

            if (conversation && data.participant != "Authorized Participant") {
              this.$store.commit("video/removeParticipant", {
                ...data,
                callback_data: {
                  ParticipantSid: data.participant_id
                }
              });
            }
          }
        });
        socket.on("createAttachment", (attachment) => {
          console.log("[IO:createAttachment] " + JSON.stringify(attachment));
          this.upsertAttachment(attachment);
        });
        socket.on("updateAttachment", (attachment) => {
          console.log("[IO:updateAttachment] " + JSON.stringify(attachment));
          this.upsertAttachment(attachment);
        });
        socket.on("updateAgency", (agency) => {
          console.log("[IO:updateAgency] " + JSON.stringify(agency));
          this.updateAgency(agency);
        });
        socket.on("updateAgencySettings", (settings) => {
          console.log("[IO:updateAgencySettings] " + JSON.stringify(settings));
          this.updateAgencySettings(settings);
        });

        socket.on("unassignConversationAgent", (resp) => {
          console.log("[IO:unassignConversationAgent]", resp);
          this.$store.commit("conversations/addConversation", resp);
        });

        socket.on("assignConversationAgent", (resp) => {
          console.log("[IO:assignConversationAgent]", resp);
          this.$store.commit("conversations/addConversation", resp);
        });

        socket.on("updateRecipient", (data) => {
          const { recipient_id } = data;
          if (this.currentConversation !== null && this.currentConversation.recipients) {
            this.$store.commit("conversations/updateRecipient", data);
          }
        });

        socket.on("updateRecipientLanguage", (data) => {
          console.log("[IO:updateRecipientLanguage]", data);
          if (this.currentConversation.status !== null && this.currentConversation.recipients) {
            this.$store.commit("conversations/updateRecipient", data);
            const { number, language } = data;
            // const recogLang = recognitionLanguages.find(item => item.split('-')[0] == language);
            if (number) {
              const option = {};
              option[number] = language
              this.$store.commit("transcription/setRecognitionLanguages", option);
              socket.emit("share-information", {
                conversationId: this.currentConversation.conversation_id,
                type: 'recognition-language',
                data: option,
              })
            }
          }
        });

        socket.on("sdk:receive-transcription", (data) => {
          if (this.videoRoomWidgetBootstraped) {
            if (!this.transcribing) {
              EventBus.$emit("start-transcription");
            } else {
              EventBus.$emit("sdk:receive-transcription", data);
            }
          }
        })

        socket.on("video:receive-transcription", (data) => {
          if (this.videoRoomWidgetBootstraped) {
            if (!this.transcribing) {
              EventBus.$emit("start-transcription");
            } else {
              EventBus.$emit("video:receive-transcription", data);
            }
          }
        })

        socket.on("transcriptionCallback", (resp) => {
          console.log("[IO:transcriptionCallback]", resp);
          this.$store.commit("conversations/addTranscription", resp);
        });

        socket.on("agentOnline", (agent) => {
          console.log("[IO:agentOnline]", agent);
          this.$store.commit("agency/addAvailableAgent", agent);
        });

        socket.on("agentOffline", (params) => {
          console.log("[IO:agentOffline]", params);
          this.$store.commit("agency/removeAvailableAgent", params);
        });

        socket.on("agentHeartbeat", (agent) => {
          this.$store.commit("agency/touchAvailableAgent", agent);
        });

        socket.on("deleteSession", (sess) => {
          console.log("[IO:deleteSession]", sess);
          this.$store.commit("agency/removeAvailableAgent", { socketId: sess.socket_id });
        });

        socket.on("addRecipientToConversation", (data) => {
          console.log("[IO:addRecipientToConversation]", data);
          if (Array.isArray(data)) {
            data.forEach((recipient) => {
              this.$store.commit("conversations/addRecipient", recipient);
            });
          } else {
            this.$store.commit("conversations/addRecipient", data);
          }
        });

        socket.on("removeRecipientFromConversation", (resp) => {
          console.log("[IO:removeRecipientFromConversation]", resp);
          this.$store.commit("conversations/removeRecipient", resp);
        });

        socket.on("receive-share-information", (data) => {
          console.log("[IO:receive-share-information]", data);
          if (data.type == 'recognition-language') {
            this.$store.commit("transcription/setRecognitionLanguages", data.data);
          }
          if (data.type == 'share-video-settings') {
            const { identity, mVideoSettings } = data.data;
            if (identity && mVideoSettings) {
              const settings = {};
              settings[identity] = { ...mVideoSettings };
              this.$store.commit("video/setVideoRoomMediaSettings", { conversationId: data.conversationId, settings });
            }
          }
        });

        socket.on("visibilityChange", (data) => {
          this.$store.commit("video/updateVideoVisibilityStatus", {
            identity: data.identity,
            visibility: data.data.visibility
          });
        });

        socket.on("updateConversation", (data) => {
          console.log("[IO:updateConversation]: ", data);
          this.$store.commit("conversations/updateConversation", data);
          if (this.currentConversation && data.conversation_id === this.currentConversation.conversation_id) {
            this.$store.commit("conversations/setConversation", data);
          }
        });

        socket.on("createCampaignEvent", (data) => {
          if (data.length > 0) {
            const { campaign_id } = data[0];
            if (campaign_id === this.$route.params.campaignId) {
              this.upsertCampaignEvent(data[0]);
            }
          }
        });

        EventBus.$on("fileUpload", (fileStream, fileKey, contentType) => {
          socket.emit("fileUpload", fileStream, fileKey, contentType, (status, result) => {
            EventBus.$emit("fileUploadStatus", status, result);
          });
        });

        EventBus.$on("video.remote-control", (data) => {
          socket.emit("remoteControl", data);
        });

        EventBus.$on("chat.send-message", (data) => {
          socket.emit("send-message", { direction: "outbound", ...data });
        });

        EventBus.$on("sdk:send-transcription", (data) => {
          socket.emit("sdk:send-transcription", data);
        });

        EventBus.$on("video:send-transcription", (data) => {
          socket.emit("video:send-transcription", data);
        });

        EventBus.$on("request-start-transcription", (data) => {
          socket.emit("request-start-transcription", data)
        })

        EventBus.$on("request-stop-transcription", (data) => {
          socket.emit("request-stop-transcription", data)
        })

        EventBus.$on("share-information", (data) => {
          socket.emit("share-information", data)
        })
      },
  },
  created() {
    const sessionId = this.$store.state.user.session;

    this.tryConnect(
      process.env.VUE_APP_SOCKET_URI,
      this.namespace,
      {
        sessionId,
      },
      this.wsOnly,
      this.path,
      this.parser
    );

    this.$store.commit("sentiment/cleanSentimentScores");
  }
};
</script>
